import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { GitBranch, Plus, Trash2, Save, X, Target, ArrowRightCircle, LayoutTemplate, ChevronRight, BookmarkPlus } from "lucide-react";
import { toast } from "sonner";

const blankStage = () => ({ id: crypto.randomUUID(), title: "Nova fase", objective: "", questions: [], exit_criteria: "" });

export default function Funnels() {
  const [funnels, setFunnels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);

  const load = () =>
    api.get("/funnels").then((r) => {
      setFunnels(r.data);
      if (r.data[0] && !activeId) { setActiveId(r.data[0].id); setDraft(structuredClone(r.data[0])); }
    });

  const loadTemplates = () => api.get("/funnel-templates").then((r) => setTemplates(r.data));
  useEffect(() => { load(); loadTemplates(); }, []);

  function select(f) { setActiveId(f.id); setDraft(structuredClone(f)); }

  async function applyTemplate(t) {
    try {
      const { data } = await api.post("/funnels/from-template", { template_id: t.id });
      await load();
      select(data);
      setTplOpen(false);
      toast.success(`Guião "${t.name}" criado`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao criar a partir do modelo");
    }
  }

  async function saveAsTemplate() {
    if (!draft) return;
    try {
      await api.post(`/funnels/${draft.id}/save-as-template`, { name: draft.name });
      await loadTemplates();
      toast.success("Guião guardado como modelo da equipa");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao guardar modelo");
    }
  }

  async function deleteTemplate(t, e) {
    e.stopPropagation();
    try {
      await api.delete(`/templates/${t.id}`);
      await loadTemplates();
      toast.success("Modelo eliminado");
    } catch (err) {
      toast.error("Falha ao eliminar");
    }
  }

  function updateStage(i, key, val) {
    const stages = [...draft.stages];
    stages[i] = { ...stages[i], [key]: val };
    setDraft({ ...draft, stages });
  }
  function updateQuestion(si, qi, val) {
    const stages = [...draft.stages];
    const questions = [...stages[si].questions]; questions[qi] = val;
    stages[si] = { ...stages[si], questions }; setDraft({ ...draft, stages });
  }
  function addQuestion(si) {
    const stages = [...draft.stages];
    stages[si] = { ...stages[si], questions: [...stages[si].questions, ""] };
    setDraft({ ...draft, stages });
  }
  function removeQuestion(si, qi) {
    const stages = [...draft.stages];
    stages[si] = { ...stages[si], questions: stages[si].questions.filter((_, i) => i !== qi) };
    setDraft({ ...draft, stages });
  }
  function addStage() { setDraft({ ...draft, stages: [...draft.stages, blankStage()] }); }
  function removeStage(i) { setDraft({ ...draft, stages: draft.stages.filter((_, idx) => idx !== i) }); }

  async function save() {
    setSaving(true);
    try {
      const payload = { name: draft.name, stages: draft.stages };
      await api.put(`/funnels/${draft.id}`, payload);
      toast.success("Funil guardado");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao guardar");
    } finally { setSaving(false); }
  }

  async function createFunnel() {
    const { data } = await api.post("/funnels", { name: "Novo Funil", stages: [blankStage()] });
    await load(); select(data);
    toast.success("Funil criado");
  }

  async function deleteFunnel() {
    if (funnels.length <= 1) return toast.error("Mantenha pelo menos um funil.");
    await api.delete(`/funnels/${draft.id}`);
    const remaining = funnels.filter((f) => f.id !== draft.id);
    setActiveId(remaining[0]?.id); setDraft(remaining[0] ? structuredClone(remaining[0]) : null);
    await load(); toast.success("Funil eliminado");
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <GitBranch className="w-7 h-7 text-[#2563EB]" /> Funil & Guiões
          </h1>
          <p className="text-[#94A3B8] text-sm mt-1">Configure as fases do seu processo de venda e as perguntas-chave de cada etapa.</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={tplOpen} onOpenChange={setTplOpen}>
            <DialogTrigger asChild>
              <Button data-testid="templates-button" variant="outline" className="border-white/10 bg-transparent hover:bg-white/5">
                <LayoutTemplate className="w-4 h-4 mr-1.5" /> Modelos de guião
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#111827] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display">Modelos de guião por setor</DialogTitle>
                <DialogDescription className="text-[#94A3B8]">Arranque mais rápido com um funil pronto e depois personalize-o.</DialogDescription>
              </DialogHeader>
              {templates.some((t) => !t.builtin) && (
                <>
                  <div className="cue-label text-[#64748B]">Os meus modelos</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {templates.filter((t) => !t.builtin).map((t) => (
                      <button
                        key={t.id}
                        onClick={() => applyTemplate(t)}
                        data-testid={`template-card-${t.id}`}
                        className="relative text-left rounded-xl border border-[#10B981]/30 bg-[#0B0F17] p-4 hover:border-[#10B981]/60 transition-colors group"
                      >
                        <button onClick={(e) => deleteTemplate(t, e)} data-testid={`delete-template-${t.id}`} className="absolute top-3 right-3 text-[#475569] hover:text-[#EF4444]">
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <div className="cue-label text-[#10B981] mb-1">Modelo da equipa</div>
                        <div className="font-display font-bold text-white pr-6">{t.name}</div>
                        <p className="text-xs text-[#94A3B8] mt-1.5 leading-relaxed">{t.description}</p>
                        <div className="mt-3 text-xs text-[#64748B]">{t.stages.length} fases</div>
                      </button>
                    ))}
                  </div>
                  <div className="cue-label text-[#64748B] mt-2">Modelos por setor</div>
                </>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {templates.filter((t) => t.builtin).map((t) => (
                  <button
                    key={t.id}
                    onClick={() => applyTemplate(t)}
                    data-testid={`template-card-${t.id}`}
                    className="text-left rounded-xl border border-white/10 bg-[#0B0F17] p-4 hover:border-[#2563EB]/50 transition-colors group"
                  >
                    <div className="cue-label text-[#2563EB] mb-1">{t.sector}</div>
                    <div className="font-display font-bold text-white">{t.name}</div>
                    <p className="text-xs text-[#94A3B8] mt-1.5 leading-relaxed">{t.description}</p>
                    <div className="mt-3 flex items-center gap-1 text-xs text-[#64748B]">
                      {t.stages.length} fases
                      <ChevronRight className="w-3.5 h-3.5 ml-auto text-[#2563EB] opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))}
              </div>
            </DialogContent>
          </Dialog>
          <Button onClick={createFunnel} data-testid="create-funnel-button" className="bg-[#2563EB] hover:bg-[#1D4ED8]">
            <Plus className="w-4 h-4 mr-1.5" /> Novo funil
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {funnels.map((f) => (
          <button
            key={f.id}
            onClick={() => select(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              f.id === activeId ? "bg-[#2563EB] text-white" : "bg-[#111827] border border-white/10 text-[#94A3B8] hover:text-white"
            }`}
          >
            {f.name}
          </button>
        ))}
      </div>

      {draft && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              data-testid="funnel-name-input"
              className="max-w-sm bg-[#111827] border-white/10 font-display font-bold text-lg h-11"
            />
            <Button onClick={save} disabled={saving} data-testid="save-funnel-button" className="bg-[#10B981] hover:bg-[#059669] h-11">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "A guardar…" : "Guardar"}
            </Button>
            <Button onClick={saveAsTemplate} data-testid="save-as-template-button" variant="outline" className="border-white/10 bg-transparent hover:bg-white/5 h-11">
              <BookmarkPlus className="w-4 h-4 mr-1.5" /> Guardar como modelo
            </Button>
            <Button onClick={deleteFunnel} variant="outline" className="border-[#EF4444]/40 bg-transparent text-[#EF4444] hover:bg-[#EF4444]/10 h-11">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {draft.stages.map((s, i) => (
              <div key={s.id} className="rounded-2xl border border-white/10 bg-[#111827] p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-[#2563EB]/20 text-[#2563EB] flex items-center justify-center font-mono text-sm font-bold">{i + 1}</div>
                  <Input
                    value={s.title}
                    onChange={(e) => updateStage(i, "title", e.target.value)}
                    data-testid={`stage-title-input-${i}`}
                    className="flex-1 bg-[#0B0F17] border-white/10 font-display font-semibold"
                  />
                  <button onClick={() => removeStage(i)}><X className="w-4 h-4 text-[#475569] hover:text-[#EF4444]" /></button>
                </div>
                <div>
                  <label className="cue-label text-[#64748B] flex items-center gap-1"><Target className="w-3 h-3" /> Objetivo</label>
                  <Textarea value={s.objective} onChange={(e) => updateStage(i, "objective", e.target.value)} className="mt-1 bg-[#0B0F17] border-white/10 text-sm min-h-[54px] resize-none" />
                </div>
                <div>
                  <label className="cue-label text-[#64748B]">Perguntas-chave</label>
                  <div className="space-y-1.5 mt-1">
                    {s.questions.map((q, qi) => (
                      <div key={qi} className="flex gap-1.5">
                        <Input value={q} onChange={(e) => updateQuestion(i, qi, e.target.value)} className="bg-[#0B0F17] border-white/10 text-sm h-9" />
                        <button onClick={() => removeQuestion(i, qi)}><Trash2 className="w-4 h-4 text-[#475569] hover:text-[#EF4444]" /></button>
                      </div>
                    ))}
                    <Button onClick={() => addQuestion(i)} variant="ghost" size="sm" className="text-[#2563EB] h-8 text-xs">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar pergunta
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="cue-label text-[#64748B] flex items-center gap-1"><ArrowRightCircle className="w-3 h-3" /> Critério para avançar</label>
                  <Input value={s.exit_criteria} onChange={(e) => updateStage(i, "exit_criteria", e.target.value)} className="mt-1 bg-[#0B0F17] border-white/10 text-sm h-9" />
                </div>
              </div>
            ))}
            <button onClick={addStage} data-testid="add-stage-button" className="rounded-2xl border border-dashed border-white/15 bg-[#111827]/50 p-5 flex flex-col items-center justify-center gap-2 text-[#64748B] hover:text-[#2563EB] hover:border-[#2563EB]/40 transition-colors min-h-[180px]">
              <Plus className="w-8 h-8" />
              <span className="text-sm font-medium">Adicionar fase</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
